import React, { useEffect, useState } from 'react'
import { getAssetUrl } from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

import {
    Box,
    Text,
    Image,
    Divider,

    /* Hooks */
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'

/* Styles */
import { useMyRewardsStyles } from '../styles/myRewardsStyles'

/* Hooks */
import { useOptimizely } from '../../../contexts/optimizely-context'

/* Utils */
import { generateStyles } from '../../../components/optimizely/utils/utils'

const HowItWorks = () => {
    const { rewardPageBlock } = useOptimizely()
    const styles = useMyRewardsStyles()
    const isMobile = useBreakpointValue({ base: true, lg: false });
    const hiwStyle = styles?.components?.howItWorks
    const [cmsData, setCmsData] = useState(null)

    const breakpointTitle = useBreakpointValue({
        base: cmsData?.mobileText,
        md: cmsData?.tabletText,
        lg: cmsData?.desktopText
    })

    // This will fallback CMS
    const hardCodedData = {
        title: {
            desktopText: 'How it <span class="highlighted">works</span>',
            tabletText: 'How it <span class="highlighted">works</span>',
            mobileText: 'How it <span class="highlighted">works</span>',
        },
        tiles: [{
            icon: getAssetUrl('static/img/my-rewards/earn-points.svg'),
            title: 'EARN POINTS',
            subtitle: 'Earn points by shopping in-store & online, downloading our app, & more!'
        }, {
            icon: getAssetUrl('static/img/my-rewards/more-benefits.svg'),
            title: 'UNLOCK MORE BENEFITS',
            subtitle: "The more points you earn, the higher up in the ranks you'll be, which means even more benefits."
        }, {
            icon: getAssetUrl('static/img/my-rewards/redeem-points.svg'),
            title: 'REDEEM YOUR POINTS',
            subtitle: 'Redeem your points to save cash on your purchases in-store and online!'
        }],
        subTilesBgColor: '#373637',
        subTilesDivider: true,
        subTiles: [{
            icon: getAssetUrl('static/img/my-rewards/right-arrow.svg'),
            title: 'EARN POINTS',
            subtitle: '$1 spent = 1 point'
        }, {
            icon: getAssetUrl('static/img/my-rewards/right-arrow.svg'),
            title: 'redeem points',
            subtitle: '150 points = $5 off'
        }]
    }

    const cmsDataModel = (rewardPageBlockModel) => {
        let title = null
        let subTilesBgColor = null
        let subTilesDivider = false
        const tiles = []
        const subTiles = []

        if (rewardPageBlockModel?.RewardTitleResponsiveText) {
            const titleExpanded = rewardPageBlockModel?.RewardTitleResponsiveText?.Expanded
            title = {
                mobileText: titleExpanded?.MobileText,
                tabletText: titleExpanded?.TabletText,
                desktopText: titleExpanded?.DesktopText,
                ResponsiveTextBlockTextStyle: titleExpanded?.ResponsiveTextBlockTextStyle
            }
        }

        rewardPageBlockModel?.RewardTileList?.forEach((item) => {
            const expanded = item?.ContentLink?.Expanded

            if (expanded?.__typename === 'RewardTileBlock') {
                tiles.push({
                    icon: expanded?.IconMedia?.Expanded?.Url,
                    alt: expanded?.IconAltText !== '' ? expanded?.IconAltText : 'Icon',
                    title: expanded?.Title,
                    subtitle: expanded?.Description,
                    DescriptionBorderColor: expanded?.DescriptionBorderColor,
                    DescriptionBorderWidth: expanded?.DescriptionBorderWidth,
                    RewardTileBlockDescriptionStyle: expanded?.RewardTileBlockDescriptionStyle,
                    RewardTileBlockTitleStyle: expanded?.RewardTileBlockTitleStyle
                })
            }

            if (expanded?.__typename === 'RewardTileListBlock') {
                subTilesBgColor = expanded?.BackgroundColor
                subTilesDivider = expanded?.Divider
                expanded?.RewardTileList?.forEach((listItem) => {
                    const tileExpanded = listItem?.ContentLink?.Expanded
                    subTiles.push({
                        icon: tileExpanded?.IconMedia?.Expanded?.Url,
                        alt: tileExpanded?.IconAltText !== '' ? tileExpanded?.IconAltText : 'Icon',
                        title: tileExpanded?.Title,
                        subtitle: tileExpanded?.Description,
                        DescriptionBorderColor: tileExpanded?.DescriptionBorderColor,
                        DescriptionBorderWidth: tileExpanded?.DescriptionBorderWidth,
                        RewardTileBlockDescriptionStyle: tileExpanded?.RewardTileBlockDescriptionStyle,
                        RewardTileBlockTitleStyle: tileExpanded?.RewardTileBlockTitleStyle
                    })
                })
            }
        })

        return {
            ...title,
            tiles,
            subTilesBgColor,
            subTilesDivider,
            subTiles
        }
    }

    useEffect(() => {
        if (rewardPageBlock) {
            setCmsData(cmsDataModel(rewardPageBlock))
        } else {
            setCmsData(hardCodedData)
        }
    }, [rewardPageBlock])

    return (
        <Box {...hiwStyle.container}>
            <Box
                sx={styles.highlighted}
                {...generateStyles(cmsData?.ResponsiveTextBlockTextStyle, {...styles.sectionTitle})}
                dangerouslySetInnerHTML={{ __html: breakpointTitle }} />
            <Box {...hiwStyle.tilesContainer}>
                <Box {...hiwStyle.tiles} {...styles.componentWrapper}>
                    {cmsData?.tiles.map((tile, tileIndex) => {
                        return (
                            <Box {...hiwStyle.tile} key={`hiw-tile-${tileIndex}`}>
                                <Image {...hiwStyle.tileIcon} src={tile.icon} alt={tile?.alt} />
                                <Text {...generateStyles(tile?.RewardTileBlockTitleStyle, {...hiwStyle.tileTitle})}>
                                    {tile.title}
                                </Text>
                                <Text {...generateStyles(tile?.RewardTileBlockDescriptionStyle, {...hiwStyle.tileSubtitle})}
                                    {...(tile?.DescriptionBorderColor || tile?.DescriptionBorderWidth) && {
                                        border: `${tile?.DescriptionBorderWidth || '1px'} solid`,
                                        borderColor: tile?.DescriptionBorderColor || 'black',
                                        p: '1px 8px 4px',
                                        lineHeight: '100%'
                                    }}>
                                    {tile.subtitle}
                                </Text>
                            </Box>
                        )
                    })}
                </Box>
            </Box>
            <Box {...hiwStyle.subTilesContainer} backgroundColor={cmsData?.subTilesBgColor}>
                <Box {...hiwStyle.subTiles} {...styles.componentWrapper}>
                    {cmsData?.subTiles.map((subTile, subTileIndex) => {
                        return (
                            <>
                                <Box {...hiwStyle.contentWrapper} key={`hiw-subtile-${subTileIndex}`}>
                                    <Box {...hiwStyle.subTile}>
                                        <Text {...generateStyles(subTile?.RewardTileBlockTitleStyle, {...hiwStyle.subTileTitle})}>
                                            <Image {...hiwStyle.subTileIcon} src={subTile.icon} alt={subTile?.alt} />
                                            {subTile.title}
                                        </Text>
                                        <Text {...hiwStyle.subTileSubtitle} {...generateStyles(subTile?.RewardTileBlockDescriptionStyle, {...hiwStyle.subTileSubtitle})}
                                            {...(subTile?.DescriptionBorderColor || subTile?.DescriptionBorderWidth) && {
                                                border: `${subTile?.DescriptionBorderWidth || '1px'} solid`,
                                                borderColor: subTile?.DescriptionBorderColor || 'black',
                                                p: '1px 8px 4px',
                                                lineHeight: '100%',
                                                margin: '0 auto',
                                                width: 'fit-content',
                                                fontSize: '10px',
                                                fontWeight: 'medium'
                                            }}>
                                            {subTile.subtitle}
                                        </Text>
                                    </Box>
                                </Box>
                                {cmsData?.subTilesDivider && subTileIndex !== cmsData?.subTiles.length - 1 &&
                                    <Divider {...hiwStyle.divider} orientation={isMobile ? 'horizontal' : 'vertical'} />
                                }
                            </>
                        )
                    })}
                </Box>
            </Box>
        </Box>
    )
}

export default HowItWorks
